import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { PushNotifications } from "@capacitor/push-notifications";
import backofficeService from "@/api/backoffice";
import router from "@/router";
import { isNativeMobileApp } from "./runtime";

const installationKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobileInstallationId`;
const registeredKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobilePushRegistered`;
const notificationHistoryKey = `${process.env.VUE_APP_STORAGE_PREFIX}mobileNotificationHistory`;
const notificationHistoryLimit = 200;
let listenersInitialized = false;
let resolveRegistration;
let rejectRegistration;
const PushConfiguration = registerPlugin("PushConfiguration");

async function isNativePushConfigured() {
  if (!isNativeMobileApp || Capacitor.getPlatform() !== "android") return isNativeMobileApp;
  if (!Capacitor.isPluginAvailable("PushConfiguration")) return false;
  try {
    const { configured } = await PushConfiguration.getStatus();
    return configured === true;
  } catch (error) {
    return false;
  }
}

function navigateFromPayload(payload = {}) {
  const path = mobileNotificationAction(payload).path;
  if (path && path !== router.currentRoute.fullPath) {
    router.push(path).catch(() => {});
  }
}

export function mobileNotificationAction(notification = {}) {
  const data = notification.data || {};
  const actionType = String(notification.actionType || data.actionType || "").trim().toLowerCase();
  const recordId = String(notification.recordId || data.recordId || "").trim();
  const numericRecordId = Number(recordId);
  if (!/^\d+$/.test(recordId) || !Number.isSafeInteger(numericRecordId) || numericRecordId < 1) return {};
  if (actionType === "invoice_receipt") {
    return { actionType, recordId, label: "View receipt", path: `/invoice/list?receiptId=${encodeURIComponent(recordId)}` };
  }
  const locationId = String(notification.locationId || data.locationId || "").trim();
  const numericLocationId = Number(locationId);
  if (actionType === "shift_summary" && /^\d+$/.test(locationId) && Number.isSafeInteger(numericLocationId) && numericLocationId > 0) {
    return { actionType, recordId, label: "View shift summary", path: `/mobile-actions/shift/${encodeURIComponent(recordId)}?locationId=${encodeURIComponent(locationId)}` };
  }
  return {};
}

function safeNotificationRecord(notification = {}) {
  const data = notification.data || {};
  const route = data.path || data.route || data.url || notification.route || "";
  const receivedAt = notification.createdAt || notification.receivedAt || data.createdAt || new Date().toISOString();
  const title = String(notification.title || "RepairNest notification").slice(0, 140);
  const body = String(notification.body || "").slice(0, 500);
  const organization = typeof (data.organizationName || data.organization || notification.organization) === "string"
    ? (data.organizationName || data.organization || notification.organization).slice(0, 120)
    : "";
  const location = typeof (data.locationName || data.location || notification.location) === "string"
    ? (data.locationName || data.location || notification.location).slice(0, 120)
    : "";
  const safeRoute = typeof route === "string" && (route.startsWith("/") || /^https:\/\/admin\.repairnestpos\.com\//i.test(route))
    ? route.slice(0, 500)
    : "";
  const id = String(data.notificationId || notification.id || `${title}|${body}|${safeRoute}|${receivedAt.slice(0, 16)}`).slice(0, 600);
  const iconKey = typeof (data.iconKey || notification.iconKey) === "string"
    ? (data.iconKey || notification.iconKey).slice(0, 80)
    : "";
  const action = mobileNotificationAction(notification);
  return {
    id,
    type: String(notification.type || data.type || "").slice(0, 80),
    iconKey,
    actionType: action.actionType || "",
    recordId: action.recordId || "",
    title,
    body,
    organization,
    location,
    locationId: notification.locationId || data.locationId || null,
    route: safeRoute,
    readAt: notification.readAt || null,
    receivedAt,
  };
}

function saveNotification(notification) {
  if (!localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)) return null;
  const record = safeNotificationRecord(notification);
  let history = [];
  try {
    history = JSON.parse(localStorage.getItem(notificationHistoryKey) || "[]");
    if (!Array.isArray(history)) history = [];
  } catch (error) {
    history = [];
  }
  const existing = history.find((item) => item && item.id === record.id);
  if (existing && existing.receivedAt) record.receivedAt = existing.receivedAt;
  history = [record, ...history.filter((item) => item && item.id !== record.id)].slice(0, notificationHistoryLimit);
  localStorage.setItem(notificationHistoryKey, JSON.stringify(history));
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed"));
  return record;
}

export function getMobileNotificationHistory() {
  try {
    const history = JSON.parse(localStorage.getItem(notificationHistoryKey) || "[]");
    return Array.isArray(history) ? history.slice(0, notificationHistoryLimit) : [];
  } catch (error) {
    return [];
  }
}

export function cacheMobileNotificationHistory(notifications = []) {
  const cached = getMobileNotificationHistory();
  const merged = notifications
    .map((notification) => safeNotificationRecord(notification))
    .concat(cached)
    .filter((notification, index, all) => all.findIndex((item) => item.id === notification.id) === index)
    .sort((left, right) => new Date(right.receivedAt) - new Date(left.receivedAt))
    .slice(0, notificationHistoryLimit);
  localStorage.setItem(notificationHistoryKey, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed"));
  return merged;
}

export async function getServerNotificationHistory(params = {}) {
  const response = await backofficeService.Api().get("mobile/notifications", { params });
  const payload = response.data || {};
  return {
    items: Array.isArray(payload.data) ? payload.data.map((item) => safeNotificationRecord(item)) : [],
    count: Number(payload.count) || 0,
    unreadCount: Number(payload.unreadCount) || 0,
    limit: Number(payload.limit) || Number(params.limit) || 20,
    offset: Number(payload.offset) || 0,
  };
}

export async function markServerNotificationRead(id) {
  if (!id) return null;
  const response = await backofficeService.Api().patch(`mobile/notifications/${encodeURIComponent(id)}/read`);
  return response.data && response.data.data ? safeNotificationRecord(response.data.data) : null;
}

export async function getShiftClosePushPreference(locationId) {
  const response = await backofficeService.Api().get("mobile/preferences/shift-close", { params: { locationId } });
  return response.data && response.data.data
    ? response.data.data
    : { locationId, enabled: false };
}

export async function setShiftClosePushPreference(locationId, enabled) {
  const response = await backofficeService.Api().put("mobile/preferences/shift-close", {
    locationId,
    enabled: Boolean(enabled),
  });
  return response.data && response.data.data ? response.data.data : null;
}

export function clearMobileNotificationHistory() {
  localStorage.removeItem(notificationHistoryKey);
  window.dispatchEvent(new CustomEvent("repairnest-notification-history-changed"));
}

async function getInstallationId() {
  let installationId = localStorage.getItem(installationKey);
  if (!installationId) {
    installationId = (await Device.getId()).identifier;
    localStorage.setItem(installationKey, installationId);
  }
  return installationId;
}

export async function initializeMobileNotifications() {
  if (!isNativeMobileApp || listenersInitialized) return;
  if (!(await isNativePushConfigured())) return;
  listenersInitialized = true;
  await PushNotifications.addListener("registration", async ({ value: pushToken }) => {
    try {
      const [{ version: appVersion }, installationId] = await Promise.all([App.getInfo(), getInstallationId()]);
      await backofficeService.Api().post("mobile/devices", {
        installationId,
        pushToken,
        platform: Capacitor.getPlatform(),
        appVersion,
      });
      localStorage.setItem(registeredKey, "true");
      if (resolveRegistration) resolveRegistration();
    } catch (error) {
      localStorage.removeItem(registeredKey);
      if (rejectRegistration) rejectRegistration(error);
      console.error("Unable to save this device for notifications", error); // eslint-disable-line no-console
    }
  });
  await PushNotifications.addListener("registrationError", (error) => {
    if (rejectRegistration) rejectRegistration(error);
    console.error("Push notification registration failed", error); // eslint-disable-line no-console
  });
  await PushNotifications.addListener("pushNotificationReceived", (notification) => {
    saveNotification(notification);
  });
  await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    saveNotification(notification);
    navigateFromPayload(notification.data);
  });
  await App.addListener("appUrlOpen", ({ url }) => navigateFromPayload({ url }));
  listenersInitialized = true;
}

export async function getMobileNotificationStatus() {
  if (!isNativeMobileApp) return "unsupported";
  if (!(await isNativePushConfigured())) return "unavailable";
  const permission = await PushNotifications.checkPermissions();
  return permission.receive === "granted" && localStorage.getItem(registeredKey) === "true"
    ? "enabled"
    : permission.receive;
}

export async function enableMobileNotifications() {
  if (!isNativeMobileApp) return "unsupported";
  if (!(await isNativePushConfigured())) return "unavailable";
  await initializeMobileNotifications();
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== "granted") permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") return "denied";
  const registration = new Promise((resolve, reject) => {
    resolveRegistration = resolve;
    rejectRegistration = reject;
    setTimeout(() => reject(new Error("Notification registration timed out")), 15000);
  });
  try {
    await PushNotifications.register();
    await registration;
  } finally {
    resolveRegistration = null;
    rejectRegistration = null;
  }
  return "enabled";
}

export async function unregisterMobileDevice() {
  if (!isNativeMobileApp) return;
  const installationId = localStorage.getItem(installationKey);
  if (installationId) {
    await backofficeService.Api().delete(`mobile/devices/${encodeURIComponent(installationId)}`);
  }
  localStorage.removeItem(registeredKey);
}
